"use client";

import * as React from "react";
import { Maximize2Icon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const BASE_CLASS =
  "flex field-sizing-fixed min-h-8 w-full resize-y rounded-lg border border-input bg-transparent px-2.5 py-2 text-base outline-none placeholder:text-muted-foreground focus-visible:border-[#333] focus-visible:ring-0 disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40";

type TextareaProps = React.ComponentProps<"textarea"> & {

  expandable?: boolean;

  expandTitle?: string;
};

function Textarea({
  className,
  ref,
  expandable,
  expandTitle,
  ...props
}: TextareaProps) {
  const [expanded, setExpanded] = React.useState(false);

  const field = (
    <textarea
      ref={ref}
      data-slot="textarea"
      className={cn(BASE_CLASS, className)}
      {...props}
    />
  );

  if (!expandable) {
    return field;
  }

  return (
    <div className="group relative w-full">
      {field}
      {/*
        浮在文字之上，不给输入框留右边距：留白得按最长的那行留，而绝大多数时候
        右上角根本没字，等于白占一块。平时透明，鼠标进框或键盘聚焦才显出来，
        真压住字的那一瞬也有半透底衬着能看清下面
      */}
      <button
        type="button"
        aria-label="放大"
        onClick={() => setExpanded(true)}
        className="absolute top-1 right-1 rounded-md bg-background/80 p-1 text-muted-foreground opacity-0 backdrop-blur-[2px] transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 hover:text-foreground focus-visible:opacity-100"
      >
        <Maximize2Icon className="size-3.5" />
      </button>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        {/*
          尺寸按屏幕给，不按内容：内容长度天差地别，照它算要么在短文本时开出一个
          空荡荡的巨框，要么在长文本时顶到屏幕外。留出边距是为了让人看得见自己
          还在弹窗里，点外面能关掉。
        */}
        <DialogContent className="h-[85vh] w-[92vw] max-w-[1400px] grid-rows-[auto_1fr] gap-3">
          <DialogHeader>
            <DialogTitle>{expandTitle ?? "放大编辑"}</DialogTitle>
            <DialogDescription>
              这里改的内容与原输入框是同一份，关掉即生效。
            </DialogDescription>
          </DialogHeader>
          <textarea
            autoFocus
            data-slot="textarea"
            className={cn(BASE_CLASS, "h-full resize-none")}
            {...props}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { Textarea };
